import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Chip, Alert } from '@mui/material';
import { CheckCircleOutline, Star, ArrowBack, CalendarMonth, Warning } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getPreferences, updatePreferences } from '../firebase/dataHandler';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/** Returns ms remaining until expiry. Negative means already expired. */
const msUntilExpiry = (premiumExpiresAt) =>
  premiumExpiresAt ? premiumExpiresAt - Date.now() : -1;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export default function Subscription() {
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [premiumExpiresAt, setPremiumExpiresAt] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    getPreferences().then(prefs => {
      setIsPremium(prefs.isPremium === true);
      setPremiumExpiresAt(prefs.premiumExpiresAt || null);
      setSubscriptionStatus(prefs.subscriptionStatus || null);
      setInitialLoad(false);
    }).catch(err => {
      console.error(err);
      setInitialLoad(false);
    });
  }, []);

  // ── Derived expiry state ───────────────────────────────────────────────────
  const remaining = msUntilExpiry(premiumExpiresAt);
  const isExpired = isPremium === false && premiumExpiresAt && remaining < 0;
  const isExpiringSoon = isPremium && premiumExpiresAt && remaining > 0 && remaining < SEVEN_DAYS_MS;
  const daysLeft = premiumExpiresAt
    ? Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000)))
    : null;

  const formatDate = (ms) =>
    ms ? new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  // ── Subscribe handler ──────────────────────────────────────────────────────
  const handleSubscribeClick = async () => {
    setLoading(true);
    try {
      const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!res) {
        alert('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }

      const fns = getFunctions();
      const createSubscription = httpsCallable(fns, 'createRazorpaySubscription');
      const result = await createSubscription();
      const subscriptionId = result.data.subscription_id;
      const keyId = result.data.key_id;

      const options = {
        key: keyId, 
        subscription_id: subscriptionId,
        name: 'Finaculator Premium',
        description: 'Monthly Premium Subscription',
        prefill: {
          email: getAuth().currentUser?.email || '',
        },
        handler: async function (response) {
          try {
            // Optimistically update UI while webhook does the authoritative update
            const premiumExpiresAt = Date.now() + 31 * 24 * 60 * 60 * 1000;
            const prefs = await getPreferences();
            await updatePreferences({
              ...prefs,
              isPremium: true,
              premiumExpiresAt,
              subscriptionId,
              subscriptionStatus: 'active',
              lastRenewedAt: Date.now(),
            });
            setIsPremium(true);
            setPremiumExpiresAt(premiumExpiresAt);
            setSubscriptionStatus('active');
            navigate('/dashboard');
          } catch (err) {
            console.error('Failed to update status after payment:', err);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
        theme: { color: '#15C358' },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error(err);
      alert('Failed to initiate checkout.');
      setLoading(false);
    }
  };

  // ── Cancel handler ─────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose premium access at the end of your billing period.')) return;
    setLoading(true);
    try {
      const prefs = await getPreferences();
      await updatePreferences({ ...prefs, subscriptionStatus: 'cancelled' });
      setSubscriptionStatus('cancelled');
    } catch (err) {
      console.error(err);
      alert('Failed to cancel subscription. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-base)' }}>
        <CircularProgress />
      </Box>
    );
  }

  const showSubscribeBtn = !isPremium;
  const showRenewBtn = isExpired;

  return (
    <Box className="page-pad fade-in" sx={{ minHeight: '100vh', background: 'var(--bg-base)', pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ color: 'var(--text-secondary)', textTransform: 'none', mr: 2 }}
        >
          Back
        </Button>
      </Box>

      {/* Expiry / renewal alerts */}
      {isExpiringSoon && (
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 3, borderRadius: 3, fontFamily: 'Inter' }}
          action={
            <Button size="small" color="warning" variant="contained" sx={{ borderRadius: 6, fontWeight: 700, textTransform: 'none' }} onClick={handleSubscribeClick}>
              Renew Now
            </Button>
          }
        >
          Your Premium plan expires in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> on {formatDate(premiumExpiresAt)}. Renew to avoid interruption.
        </Alert>
      )}

      {isExpired && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 3, fontFamily: 'Inter' }}
          action={
            <Button size="small" color="error" variant="contained" sx={{ borderRadius: 6, fontWeight: 700, textTransform: 'none' }} onClick={handleSubscribeClick} disabled={loading}>
              {loading ? <CircularProgress size={16} color="inherit" /> : 'Re-subscribe'}
            </Button>
          }
        >
          Your Premium subscription <strong>expired on {formatDate(premiumExpiresAt)}</strong>. Premium features are currently disabled.
        </Alert>
      )}

      <Box sx={{ textAlign: 'center', mb: 6, maxWidth: '600px', mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, fontFamily: 'Inter', color: 'var(--text-primary)' }}>
          {isPremium ? "You're on Premium!" : isExpired ? "Subscription Expired" : "Upgrade to Premium"}
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: 16, fontFamily: 'Inter' }}>
          {isPremium
            ? "Thank you for subscribing! You have full access to all advanced analytics, exports, and professional tools."
            : isExpired
              ? "Your premium access has ended. Re-subscribe to regain access to advanced analytics, PDF reports, and Excel exports."
              : "Take your financial management to the next level with advanced analytics, professional PDF reports, and Excel exports."}
        </Typography>

        {/* Renewal date chip */}
        {premiumExpiresAt && !isExpired && (
          <Chip
            icon={<CalendarMonth sx={{ fontSize: 16 }} />}
            label={`Renews on ${formatDate(premiumExpiresAt)}`}
            size="small"
            sx={{ mt: 2, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontFamily: 'Inter', fontSize: 13 }}
          />
        )}

        {/* Cancellation notice */}
        {subscriptionStatus === 'cancelled' && isPremium && (
          <Chip
            label={`Access until ${formatDate(premiumExpiresAt)} · Cancelled`}
            size="small"
            color="warning"
            sx={{ mt: 2, fontFamily: 'Inter', fontSize: 13 }}
          />
        )}
      </Box>

      {/* Pricing Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', maxWidth: '900px', mx: 'auto' }}>

        {/* Free Tier */}
        <Box sx={{
          flex: '1 1 300px',
          background: 'var(--bg-card)',
          borderRadius: 4,
          p: 4,
          border: '1px solid var(--border-subtle)',
          opacity: isPremium ? 0.6 : 1
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1, color: 'var(--text-primary)' }}>Basic</Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, mb: 0.5, color: 'var(--text-primary)' }}>
            ₹0 <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>/ forever</span>
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 4, fontSize: 14 }}>Core features for tracking daily collections.</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <FeatureItem text="Unlimited Customers" />
            <FeatureItem text="Unlimited Accounts" />
            <FeatureItem text="Daily Collection Tracking" />
            <FeatureItem text="Basic Profiles" />
          </Box>

          <Button
            disabled
            fullWidth
            variant="outlined"
            sx={{ borderRadius: 8, borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}
          >
            {isPremium ? 'Downgrade' : 'Current Plan'}
          </Button>
        </Box>

        {/* Premium Tier */}
        <Box sx={{
          flex: '1 1 300px',
          background: 'var(--bg-elevated)',
          borderRadius: 4,
          p: 4,
          border: '2px solid',
          borderColor: isPremium ? '#0fd47a' : isExpired ? '#f44336' : '#15C358',
          position: 'relative',
          boxShadow: isPremium
            ? '0 8px 32px rgba(15,212,122,0.2)'
            : isExpired
              ? '0 8px 32px rgba(244,67,54,0.2)'
              : '0 8px 32px rgba(21,195,88,0.2)',
        }}>
          <Box sx={{
            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
            background: isPremium
              ? 'linear-gradient(135deg, #0fd47a, #0a9b58)'
              : isExpired
                ? 'linear-gradient(135deg, #f44336, #b71c1c)'
                : 'linear-gradient(135deg, #15C358, #109441)',
            color: '#fff', px: 2, py: 0.5, borderRadius: 10, fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
          }}>
            <Star sx={{ fontSize: 14 }} />
            {isPremium ? 'Active' : isExpired ? 'Expired' : 'Recommended'}
          </Box>

          <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1, color: isPremium ? '#0fd47a' : isExpired ? '#f44336' : '#15C358' }}>Pro</Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, mb: 0.5, color: 'var(--text-primary)' }}>
            ₹99 <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>/ month</span>
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 4, fontSize: 14 }}>Professional analytics and reporting.</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <FeatureItem text="Everything in Basic" />
            <FeatureItem text="Advanced Dashboard Analytics" highlight />
            <FeatureItem text="Export to Excel" highlight />
            <FeatureItem text="Professional PDF Reports" highlight />
            <FeatureItem text="Priority Support" highlight />
          </Box>

          {isPremium && subscriptionStatus !== 'cancelled' ? (
            <Button
              onClick={handleCancel}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: 8, borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)', textTransform: 'none' }}
              disabled={loading}
            >
              Cancel Subscription
            </Button>
          ) : showSubscribeBtn || showRenewBtn ? (
            <Button
              onClick={handleSubscribeClick}
              fullWidth
              variant="contained"
              sx={{
                borderRadius: 8,
                py: 1.5,
                background: isExpired
                  ? 'linear-gradient(135deg, #f44336, #ff7043)'
                  : 'linear-gradient(135deg, #15C358, #109441)',
                color: '#fff',
                boxShadow: isExpired
                  ? '0 4px 14px rgba(244,67,54,0.4)'
                  : '0 4px 14px rgba(21,195,88,0.4)',
                fontWeight: 700,
                textTransform: 'none',
              }}
              disabled={loading}
            >
              {loading
                ? <CircularProgress size={24} color="inherit" />
                : isExpired ? 'Re-subscribe — ₹99/mo' : 'Subscribe Now — ₹99/mo'}
            </Button>
          ) : (
            /* Cancelled but still has access until end of period */
            <Button
              onClick={handleSubscribeClick}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: 8, borderColor: '#15C358', color: '#15C358', textTransform: 'none', fontWeight: 700 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reactivate Subscription'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function FeatureItem({ text, highlight }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <CheckCircleOutline sx={{ fontSize: 18, color: highlight ? '#15C358' : 'var(--text-muted)' }} />
      <Typography sx={{ fontSize: 14, color: highlight ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: highlight ? 600 : 400 }}>
        {text}
      </Typography>
    </Box>
  );
}
